import {
  BufferGeometry,
  Color,
  Intersection,
  Mesh,
  SRGBColorSpace,
  Vector3,
} from "three";
import Delaunator from "delaunator";
import { LidarDataVisualizationZone } from "../../../types/getLiDARDataVisualizationSummariesForLastSpecificHeats";
import { convertInchToUserPreferenceToFixed } from "~/helpers/userPreference";

const SIDEWALL_THICKNESS_FACTOR = 10;
const BOTTOM_THICKNESS_FACTOR = 100;

const clockMapping: Record<number, number> = {
  3: 12,
  2: 1,
  1: 2,
  0: 3,
  [-1]: 4,
  [-2]: 5,
  [-3]: 6,
  [-4]: 7,
  [-5]: 8,
  [-6]: 9,
  5: 10,
  4: 11,
};

export const calculateDelaunayDataForSidewall = (
  zoneMappedData: LidarDataVisualizationZone[],
) => {
  const points = new Float32Array(zoneMappedData.length * 3);
  const points3d = new Array(zoneMappedData.length);
  const colorsArray = new Float32Array(zoneMappedData.length * 3);

  const color = new Color();

  for (let ip = 0; ip < zoneMappedData.length; ip++) {
    const x = zoneMappedData[ip].x / -12;
    const y = zoneMappedData[ip].y;
    const thickness = zoneMappedData[ip].thickness / SIDEWALL_THICKNESS_FACTOR;

    const index = ip * 3;

    points[index] = x;
    points[index + 1] = y;
    points[index + 2] = thickness;

    points3d[ip] = new Vector3(x, y, thickness);

    color.setRGB(
      (zoneMappedData[ip].color[0] as number) / 255,
      (zoneMappedData[ip].color[1] as number) / 255,
      (zoneMappedData[ip].color[2] as number) / 255,
      SRGBColorSpace,
    );
    colorsArray[index] = color.r;
    colorsArray[index + 1] = color.g;
    colorsArray[index + 2] = color.b;
  }

  const indexDelaunay = Delaunator.from(points3d.map((v) => [v.x, v.y]));
  const meshIndex = new Uint32Array(indexDelaunay.triangles);

  return {
    points,
    meshIndex,
    colors: colorsArray,
  };
};
export const calculateDelaunayDataForBottom = (
  zoneMappedData: LidarDataVisualizationZone[],
) => {
  const points = new Float32Array(zoneMappedData.length * 3);
  const points3d = new Array(zoneMappedData.length);
  const colorsArray = new Float32Array(zoneMappedData.length * 3);
  const color = new Color();

  for (let ip = 0; ip < zoneMappedData.length; ip++) {
    const x = zoneMappedData[ip].x * -1;
    const y = zoneMappedData[ip].y;
    const thickness = zoneMappedData[ip].thickness / BOTTOM_THICKNESS_FACTOR;

    const index = ip * 3;

    points[index] = x;
    points[index + 1] = y;
    points[index + 2] = thickness;

    points3d[ip] = new Vector3(x, y, thickness);

    color.setRGB(
      (zoneMappedData[ip].color[0] as number) / 255,
      (zoneMappedData[ip].color[1] as number) / 255,
      (zoneMappedData[ip].color[2] as number) / 255,
      SRGBColorSpace,
    );
    colorsArray[index] = color.r;
    colorsArray[index + 1] = color.g;
    colorsArray[index + 2] = color.b;
  }

  const indexDelaunay = Delaunator.from(points3d.map((v) => [v.x, v.y]));
  const meshIndex = new Uint32Array(indexDelaunay.triangles);

  return {
    points,
    meshIndex,
    colors: colorsArray,
  };
};

const calculateSidewallCourse = (yValue: number): number => {
  const min = -13.75;
  const step = 13.75 / 41;
  const scaledValue = Math.round((yValue - min) / step);
  return scaledValue == 0 ? 1 : scaledValue;
};
const calculateBottomCourse = (x: number, y: number) => {
  const bottomRadiusInFeet = 5.5;

  const totalCourse = 10;

  const amountOfIncrease = bottomRadiusInFeet / totalCourse;

  const distanceOfPoint = Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2));

  let course = Math.ceil(distanceOfPoint / amountOfIncrease);
  if (course == 0) {
    course = 1;
  }
  if (course >= totalCourse) course = totalCourse;

  return course;
};

const calculateQuadrantBottom = (xValue: number, yValue: number): number => {
  const totalQuadrant = 12;
  let result = Math.floor(
    (Math.atan2(yValue, xValue) + Math.PI / 12) /
      ((2 * Math.PI) / totalQuadrant),
  );

  if (result >= totalQuadrant / 2) {
    result = (totalQuadrant / 2) * -1;
  }
  return clockMapping[result];
};

const calculateQuadrantSidewall = (xValue: number): number => {
  const angleRange = 360;
  const segmentCount = 13;
  const segmentAngle = angleRange / segmentCount;

  const maxX = 13.75;
  const minX = -13.75;

  const normalizedX = ((xValue - minX) / (maxX - minX)) * angleRange;

  const quadrant = Math.floor(normalizedX / segmentAngle);
  const quadrantMapping = [6, 7, 8, 9, 10, 11, 12, 1, 2, 3, 4, 5, 6];
  return quadrantMapping[quadrant % segmentCount];
};
export const getMeshTooltip = (
  intersection: Intersection,
  isSidewall: boolean,
) => {
  const geometry = (intersection.object as Mesh).geometry as BufferGeometry;

  const positions = geometry.attributes.position.array as Float32Array;

  const face = intersection.face;
  if (!face) return { course: 0, quadrant: 0, thickness: 0 };

  const indexA = face.a * 3;
  const indexB = face.b * 3;
  const indexC = face.c * 3;

  const thicknessFactor = isSidewall
    ? SIDEWALL_THICKNESS_FACTOR
    : BOTTOM_THICKNESS_FACTOR;

  const thicknessA = positions[indexA + 2] * thicknessFactor;
  const thicknessB = positions[indexB + 2] * thicknessFactor;
  const thicknessC = positions[indexC + 2] * thicknessFactor;

  const course = isSidewall
    ? calculateSidewallCourse(positions[indexA + 1])
    : calculateBottomCourse(positions[indexA], positions[indexA + 1]);

  //TODO: Sidewall quadrant
  const quadrant = isSidewall
    ? calculateQuadrantSidewall(positions[indexA])
    : calculateQuadrantBottom(positions[indexA], positions[indexA + 1]);

  const thickness = (thicknessA + thicknessB + thicknessC) / 3;

  return {
    course,
    quadrant,
    thickness: convertInchToUserPreferenceToFixed(thickness, 1),
  };
};
